import { joiResolver } from "@hookform/resolvers/joi";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useForm as useFormController } from "react-hook-form";

import { participantValidationSchemas, validationOptions } from "@bujus/common";
import { useApiServiceContext } from "@bujus/common-frontend";

import { DataTable, DialogClosingButton, Form, TextField } from "@/base";
import { useSnackbarsController } from "@/hooks";

const ManyParticipantsRemovalDialog = ({ close, participants, isOpen }) => {
    const snackbarsController = useSnackbarsController();

    const queryClient = useQueryClient();

    const apiService = useApiServiceContext();

    const confirmation = `${participants.length}`;
    const formController = useFormController({
        mode: "onChange",
        resolver: joiResolver(
            participantValidationSchemas.manyRemoval.generateForm(confirmation),
            validationOptions,
        ),
        defaultValues: { confirmation: "" },
    });

    const mutation = useMutation(
        async (variables) => apiService.participants.removeManyAsync(variables.ids),
        {
            onError: (error) => {
                snackbarsController.enqueueError(error);
            },
            onSuccess: () => {
                snackbarsController.enqueueSuccessMessage(
                    `${participants.length} Teilnehmer entfernt`,
                );
                queryClient.invalidateQueries(["participants"]);
                close();
            },
        },
    );

    const tryClose = () => {
        if (mutation.isLoading) {
            return false;
        }
        close();
        return true;
    };

    const handleClose = () => {
        tryClose();
    };

    const handleFormSubmit = () => {
        if (mutation.isLoading) {
            return;
        }
        mutation.mutate({ ids: participants.map((x) => x.id) });
    };

    const handleClosingButtonClick = () => {
        tryClose();
    };

    const dataTableColumns = [
        {
            fieldName: "name.first",
            heading: "Vorname",
            getValue: (participant) => participant.name.first,
            width: 1,
        },
        {
            fieldName: "name.last",
            heading: "Nachname",
            getValue: (participant) => participant.name.last,
            width: 1,
        },
        {
            fieldName: "schoolInternalId",
            heading: "Schulinterne-ID",
            getValue: (participant) => participant.schoolInternalId,
            width: 1,
        },
    ];

    return (
        <Dialog onClose={handleClose} open={isOpen}>
            <Form controller={formController} onSubmit={handleFormSubmit}>
                <DialogTitle>{participants.length} Teilnehmer entfernen</DialogTitle>
                <DialogClosingButton
                    isDisabled={mutation.isLoading}
                    onClick={handleClosingButtonClick}
                />
                <DialogContent>
                    <DataTable columns={dataTableColumns} items={participants} />
                    <Box mt={2}>
                        <TextField
                            fieldName="confirmation"
                            isAutofocusing
                            isEnabled={!mutation.isLoading}
                            label={`Tippen Sie "${confirmation}" ein um zu bestätigen`}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <LoadingButton
                        disabled={!formController.formState.isValid}
                        icon={<DeleteIcon />}
                        loading={mutation.isLoading}
                        type="submit"
                    >
                        Entfernen
                    </LoadingButton>
                </DialogActions>
            </Form>
        </Dialog>
    );
};

export { ManyParticipantsRemovalDialog };
